import { PlayerRef, Player as RemotionPlayer } from "@remotion/player";
import { forwardRef, useEffect, useState } from "react";
import { TransitionDto } from "@repo/api/transition";
import { TransitionDialogView } from "./TransitionDialog";
import { cn } from "@/lib/utils";

type Props = {
  currentView: TransitionDialogView;
  currentTransition: TransitionDto;
  previewTransition: TransitionDto;
  useTransitionPreview: UseTransitionPreview;
  playPreview: (transition: TransitionDto) => void;
  pausePreview: () => void;
  playerRef: React.RefObject<PlayerRef | null>;
};

export type UseTransitionPreview = (transition: TransitionDto) => {
  durationInFrames: number;
  fps: number;
  Composition: React.ComponentType<any>;
  inputProps: any;
};

export const TransitionPreview = forwardRef<PlayerRef, Props>(
  (
    {
      useTransitionPreview,
      currentView,
      currentTransition,
      previewTransition,
      playPreview,
      pausePreview,
      playerRef,
    },
    ref,
  ) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const { durationInFrames, fps, Composition, inputProps } =
      useTransitionPreview(previewTransition);

    // Do not add early return to this component. Otherwise, the playerRef will not work.
    useEffect(() => {
      const player = playerRef.current;

      const setPlay = () => setIsPlaying(true);
      const setPause = () => setIsPlaying(false);

      if (player) {
        player.addEventListener("play", setPlay);
        player.addEventListener("pause", setPause);
      }

      return () => {
        if (player) {
          player.removeEventListener("play", setPlay);
          player.removeEventListener("pause", setPause);
        }
      };
    }, [playerRef]);

    return (
      <div className="h-100 w-96 shrink-0 py-6 pr-8">
        <div className="relative h-full w-full overflow-hidden rounded-lg focus-within:outline-2">
          <RemotionPlayer
            ref={ref}
            numberOfSharedAudioTags={0}
            component={Composition}
            durationInFrames={durationInFrames}
            compositionWidth={1080}
            compositionHeight={1080}
            inputProps={inputProps}
            controls={false}
            loop
            fps={fps}
            acknowledgeRemotionLicense
            style={{
              height: "100%",
              width: "100%",
            }}
          />
          <button
            className={cn(
              "absolute inset-0 flex items-center justify-center bg-black/50 text-center text-sm font-semibold text-white/90 transition-opacity",
              isPlaying
                ? "opacity-0 delay-0 duration-250"
                : "opacity-100 delay-300 duration-500",
            )}
            onMouseEnter={() => {
              playPreview(currentTransition);
            }}
            onMouseLeave={() => {
              pausePreview();
            }}
            onFocus={() => {
              playPreview(currentTransition);
            }}
            onBlur={() => {
              pausePreview();
            }}
          >
            {currentView === "TRANSITION_LIST" ? (
              <p>
                Hover transitions
                <br />
                to preview
              </p>
            ) : (
              <p>Hover to preview</p>
            )}
          </button>
        </div>
      </div>
    );
  },
);
